import styled, { keyframes, css } from "styled-components";
import { Flex, Typography, Alert, Avatar, Divider, List } from "antd";
import SbTag from "uiCore/SbTag/SbTag";
import SbCard from "uiCore/SbCard/SbCard";
import { SbSelect } from "uiCore";
import { FONT } from "./dashboard.constants";

const { Text } = Typography;

// ---- Dashboard ----

// Scrollable body under the shared PageHeader; the header band stays fixed.
// Body order: filter bar, the four widgets, then the "About this data" alert.
export const PageWrap = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px;
`;

// Shared wrapper for the two full-page empty states (access-restricted and
// no-data-yet) — a touch more bottom padding than PageWrap so the centered
// message doesn't sit flush against the viewport edge.
export const EmptyStateWrap = styled.div`
  padding: 20px 24px 40px;
`;

// Centers the empty-state message vertically within most of the viewport.
export const CenteredFlex = styled(Flex)`
  min-height: 60vh;
`;

// Sits below the widget row.
export const InfoAlert = styled(Alert)`
  margin-top: 10px;
`;

export const AboutDataText = styled.div`
  font-size: ${FONT.small}px;
`;

export const DataQualityList = styled.ul`
  margin: 4px 0 0;
  padding-left: 18px;
`;

export const FilterBarSection = styled.div`
  margin-bottom: 10px;
`;

export const SkillsTagRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const SkillTag = styled(SbTag)`
  && {
    font-size: ${FONT.small}px;
    padding: 2px 10px;
  }
`;

// Muted sub-metric label shown above a Statistic (e.g. "Potential value
// created by AI") — color matches SB_COLORS.tertiary.
export const MutedSubText = styled(Text)`
  font-size: ${FONT.small}px;
  color: ${({ theme }) => theme.colors.tertiary};
  display: block;
`;

export const MutedSubTextSpaced = styled(MutedSubText)`
  margin-top: 4px;
`;

// ---- DashboardPanel ----

// Positioned below the app header (80px) and beside the sidebar (100px) —
// see LayoutV2.styles.js HeaderStyled/SiderStyled. No slide-in transition by
// design: the source mockup toggles this panel with a plain display flip,
// not a drawer animation — so this is a plain overlay/panel pair, not an
// antd Drawer. Ant Design is used for everything *inside* it instead
// (Button, Tag, Typography, Tabs, Collapse, Select — see DashboardPanel.jsx
// and the panels/ directory).
export const Overlay = styled.div`
  position: fixed;
  top: 80px;
  left: 100px;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 400;
`;

export const Panel = styled.div`
  position: fixed;
  top: 80px;
  left: 100px;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.light};
  z-index: 410;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const PanelDot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

// Widget colour dot + title text, rendered as the PageHeader title.
export const PanelTitleStyled = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

// Scroll container spans the full panel width, so its scrollbar always sits
// flush at the panel's true right edge.
export const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
`;

// Unlike the main dashboard's Content (PageWrap above), this stays
// full-width on wide monitors — the charts inside benefit from the extra
// space rather than leaving a large centered gutter.
export const PanelContentInner = styled.div`
  padding: 24px 28px;
`;

// ---- FilterBar ----

// Brief attention pulse — played once on the applied-filter tag when a filter
// is newly applied, and a few times across the whole bar when the user clicks
// the "About this data" section's "apply a filter above" link. No existing
// pulse/highlight pattern exists elsewhere in the app to reuse, so this
// follows the app's other keyframes-based transitions (see
// TalentMobilityV2/talentMobility.animations.js) and respects reduced motion.
const pulseHighlight = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(1, 85, 231, 0.35); }
  70% { box-shadow: 0 0 0 8px rgba(1, 85, 231, 0); }
  100% { box-shadow: 0 0 0 0 rgba(1, 85, 231, 0); }
`;

export const FilterBarWrap = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  padding: 10px 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  ${({ $highlighted }) =>
    $highlighted &&
    css`
      animation: ${pulseHighlight} 600ms ease-out 3;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

export const FilterLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

export const FilterSep = styled.div`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.colors.borderLight};
`;

export const FilterLoadingIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray};
`;

export const ClearLink = styled.button`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray};
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  border: none;
  background: transparent;

  &:hover {
    background: ${({ theme }) => theme.colors.hoverBg};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const DimSelect = styled(SbSelect)`
  && {
    min-width: 160px;
  }
`;

export const ValueSelect = styled(SbSelect)`
  && {
    min-width: 180px;
  }
`;

export const FilterAppliedWrap = styled.span`
  display: inline-flex;
  border-radius: 6px;

  ${({ $pulse }) =>
    $pulse &&
    css`
      animation: ${pulseHighlight} 900ms ease-out;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

// ---- ChartCard ----

// height: 100% only takes effect when the parent Row uses align="stretch"
// (e.g. pairing a short accordion card next to a taller chart card) — it's a
// no-op otherwise, so this is safe to apply everywhere. The `styles` header
// background prop stays a plain object on SbCard itself; it's AntD's own
// semantic parts API, not a generic style override.
export const ChartCardStyled = styled(SbCard)`
  && {
    height: 100%;
  }
`;

export const TitleBlock = styled.div`
  padding: 6px 0;
`;

export const TitleText = styled.div`
  font-size: ${FONT.title}px;
  font-weight: 700;
`;

export const SubtitleText = styled(Text)`
  font-size: ${FONT.small}px;
  font-weight: 400;
`;

// ---- StatWidgetCard ----

// Shared shell for the 4 dashboard summary cards — fills the Row's stretched
// height and stacks its header/body/footer vertically (the `styles` header/
// body background prop stays a plain object on SbCard itself; it's AntD's
// own semantic parts API, not a generic style override).
export const StatCardStyled = styled(SbCard)`
  && {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

// Icon badge — background color is per-widget (WIDGET_CONFIG.*.color), so it
// stays a transient prop rather than a fixed token.
export const IconAvatar = styled(Avatar)`
  && {
    background: ${({ $bg }) => $bg};
    border-radius: 8px;
    font-size: ${FONT.small}px;
  }
`;

export const CardTitleText = styled(Text)`
  font-size: ${FONT.base}px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const CardBody = styled.div`
  flex: 1;
`;

export const CardSubText = styled(Text)`
  font-size: ${FONT.small}px;
  display: block;
  margin-top: 4px;
`;

export const CardDivider = styled(Divider)`
  && {
    margin: 8px 0 6px;
  }
`;

export const FootHintText = styled(Text)`
  font-size: ${FONT.micro}px;
`;

// "Expand ↗" label color mirrors the widget's own accent color.
export const ExpandLinkText = styled(Text)`
  font-size: ${FONT.micro}px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`;

// ---- RankedProgressBars ----

// One-off chart gridline/axis-border tone — no SB_COLORS semantic token
// matches it, so it stays a literal here rather than being forced onto an
// unrelated token.
export const GRID_COLOR = "#EDEEF2";

export const RowsWrap = styled.div`
  display: flex;
`;

// $gap varies with how many rows must fit into an optional target `height`
// (see fitRowsToHeight in RankedProgressBars.jsx).
export const LabelColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => `${$gap}px`};
`;

// Manually truncated label cell — $height/$width are per-row computed
// values (row height shrinks to fit N rows into an optional target height;
// width is the caller's labelWidth prop), so both stay transient props.
export const RowLabelText = styled(Text)`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${FONT.base}px;
  padding-right: 12px;
  flex-shrink: 0;
  height: ${({ $height }) => `${$height}px`};
  line-height: ${({ $height }) => `${$height}px`};
  width: ${({ $width }) => `${$width}px`};
  text-align: ${({ $align }) => $align};
`;

export const ChartArea = styled.div`
  flex: 1;
  position: relative;
`;

export const GridLinesRow = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: space-between;
`;

export const GridLine = styled.div`
  width: 1px;
  background: ${GRID_COLOR};
`;

export const BarsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => `${$gap}px`};
  position: relative;
`;

export const BarRow = styled.div`
  height: ${({ $height }) => `${$height}px`};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const BarTrack = styled.div`
  width: ${({ $pct }) => `${$pct}%`};
  min-width: 2px;
`;

export const BarValueText = styled(Text)`
  font-size: ${FONT.base}px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  margin-left: 8px;
  white-space: nowrap;
`;

export const AxisRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid ${GRID_COLOR};
`;

export const AxisTickText = styled(Text)`
  font-size: ${FONT.micro}px;
`;

// ---- SplitProgress ----

// Stacked two-segment bar mirroring PAIC V2 Role Impact's People/AI split bar
// (SbPACRoleImpactV2.styles.js ProgressBarTrack/ProgressBarPeople/ProgressBarAI)
// so the same People vs AI visual reads consistently across the app.
export const ProgressBarTrack = styled.div`
  display: flex;
  height: 20px;
  overflow: hidden;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.borderLight};
`;

export const ProgressBarPeople = styled.div`
  width: ${({ $width }) => `${$width}%`};
  min-width: ${({ $width }) => ((Number($width) || 0) > 0 ? "fit-content" : "0")};
  padding: ${({ $width }) => ((Number($width) || 0) > 0 ? "0 8px" : "0")};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.progressBarPeople};
  color: ${({ theme }) => theme.colors.white};
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
`;

export const ProgressBarAI = styled.div`
  width: ${({ $width }) => `${$width}%`};
  min-width: ${({ $width }) => ((Number($width) || 0) > 0 ? "fit-content" : "0")};
  padding: ${({ $width }) => ((Number($width) || 0) > 0 ? "0 8px" : "0")};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.progressBarAI};
  color: ${({ theme }) => theme.colors.dark};
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
`;

// ---- FunctionAccordion ----

export const ExtraValueText = styled(Text)`
  font-size: ${FONT.base}px;
`;

export const SubFunctionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SubFunctionRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

export const SubFunctionNameText = styled(Text)`
  font-size: ${FONT.small}px;
`;

export const SubFunctionValueText = styled(Text)`
  font-size: ${FONT.small}px;
  font-weight: 600;
`;

// ---- RoiPanel ----

// Mirrors PAIC V2 Group Agent Details' microtask expand-row pattern
// (SbPACGroupAgentDetailsV2.styles.js) — same background/spacing/typography,
// applied here to an agent's ROI detail fields instead of a microtask list.
export const ExpandedContent = styled.div`
  padding: 12px 12px 12px 54px;
  background: ${({ theme }) => theme.colors.hoverBg};
`;

export const ExpandedContentHeader = styled.div`
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 8px;
`;

export const AgentDetailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const AgentDetailRow = styled.div`
  color: ${({ theme }) => theme.colors.dark};
  font-size: 13px;
  line-height: 1.6;
`;

export const ExpandIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gray};
  transition: transform 0.18s ease;
  flex-shrink: 0;

  ${({ $expanded, theme }) =>
    $expanded &&
    css`
      transform: rotate(180deg);
      color: ${theme.colors.primary};
    `}
`;

// AntD `List.Item` normally has its own padding/border styling — this
// leaderboard renders its own row markup (RoiRow) inside instead, so the
// item shell itself is stripped down to a plain block container.
export const AgentListItem = styled(List.Item)`
  && {
    display: block;
    padding: 0;
  }
`;

export const RoiRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 0;
  cursor: pointer;
`;

// Rank badge background is per-row (top-3 accent colors, grey otherwise), so
// it stays a transient prop rather than a fixed token.
export const RankAvatar = styled(Avatar)`
  && {
    background: ${({ $bg }) => $bg};
    font-weight: 700;
    font-size: ${FONT.small}px;
  }
`;

export const AgentNameCell = styled.div`
  flex: 1;
`;

export const AgentNameText = styled(Text)`
  font-size: ${FONT.base}px;
`;

// ROI figure color mirrors the row's rank accent color.
export const RoiValueText = styled(Text)`
  font-size: ${FONT.base}px;
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

// ---- TopSkillsPanel / CollaborationPanel / TotalPotentialPanel ----
// Shared vertical stack shell — was defined identically in each panel's own
// styles file; consolidated here as one export used by all three.
export const PanelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
